import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { TimeInput } from "@heroui/react";
import { getLocalTimeZone, parseDate, Time } from "@internationalized/date";
import { useEffect, useMemo, useState } from "react";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { cn } from "@/utils";
import { motion } from "motion/react";
import { AnimatePresence } from "motion/react";
import { isMobileSafari } from "react-device-detect";
import { isNativePlatform } from "@/utils/native";

const DateTimeSection = ({
  date,
  startTime,
  endTime,
  duration,
  handleDateChange,
  handleStartTimeChange,
  handleEndTimeChange,
}: {
  date: string;
  startTime: string;
  endTime: string;
  duration: number;
  handleDateChange: (date: Date | null) => void;
  handleStartTimeChange: (time: string) => void;
  handleEndTimeChange: (time: string) => void;
}) => {
  const [fromTime, setFromTime] = useState(
    new Time(Number(startTime.split(":")[0]), Number(startTime.split(":")[1])),
  );
  const [toTime, setToTime] = useState(
    new Time(Number(endTime.split(":")[0]), Number(endTime.split(":")[1])),
  );

  const toDate = useMemo(() => {
    return dayjs(date).add(duration, "minute").format("YYYY-MM-DD");
  }, [date, duration]);

  useEffect(() => {
    if (!startTime) return;

    setFromTime(
      new Time(
        Number(startTime.split(":")[0]),
        Number(startTime.split(":")[1]),
      ),
    );
  }, [startTime]);

  useEffect(() => {
    if (!endTime) return;

    setToTime(
      new Time(Number(endTime.split(":")[0]), Number(endTime.split(":")[1])),
    );
  }, [endTime]);

  const isMultipleDays = useMemo(() => {
    return dayjs(date).add(duration, "minute").isAfter(dayjs(date), "day");
  }, [date, startTime, duration]);

  const { permissions } = useUserPermissionsStore();

  return (
    <>
      <div
        className={cn("mb-4 flex items-center gap-1", {
          "mb-2": isMultipleDays,
        })}
      >
        <LimeDatePicker
          label={isMultipleDays ? t`Datum od` : t`Datum`}
          value={parseDate(dayjs(date).format("YYYY-MM-DD"))}
          onChange={(date) => {
            if (!date) return;

            handleDateChange(date.toDate(getLocalTimeZone()));
          }}
          size="sm"
          className="flex-1"
          isDisabled={!permissions.manage_appointments}
          data-identifier="date-input"
        />

        <div className="flex gap-1">
          <TimeInput
            data-identifier="time-from-input"
            label={t`Čas od`}
            size="sm"
            value={fromTime}
            onChange={(e) => {
              if (!e) return;

              handleStartTimeChange(`${e.hour}:${e.minute}`);
              setFromTime(e);
            }}
            hourCycle={24}
            classNames={{
              input: cn({
                "text-[16px]": isMobileSafari || isNativePlatform(),
              }),
            }}
            isDisabled={!permissions.manage_appointments}
          />

          <AnimatePresence>
            {!isMultipleDays && (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0, width: 0 }}
                animate={{ opacity: 1, scale: 1, width: "auto" }}
                exit={{ opacity: 0, scale: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="shrink-0"
              >
                <TimeInput
                  label={t`Čas do`}
                  size="sm"
                  value={toTime}
                  onChange={(e) => {
                    if (!e) return;

                    handleEndTimeChange(`${e.hour}:${e.minute}`);
                    setToTime(e);
                  }}
                  hourCycle={24}
                  isDisabled={true}
                  classNames={{
                    input: cn({
                      "text-[16px]": isMobileSafari || isNativePlatform(),
                    }),
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <AnimatePresence>
        {isMultipleDays && (
          <motion.div
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mb-4 flex items-center gap-1">
              <LimeDatePicker
                label={t`Datum do`}
                value={parseDate(toDate)}
                size="sm"
                className="flex-1"
                isDisabled
              />

              <div>
                <TimeInput
                  label={t`Čas do`}
                  size="sm"
                  value={toTime}
                  hourCycle={24}
                  isDisabled
                  classNames={{
                    input: cn({
                      "text-[16px]": isMobileSafari || isNativePlatform(),
                    }),
                  }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DateTimeSection;
